import { useEffect, useRef, useState } from 'react';
import { EVT, gameEvents } from '../game/events';
import { useT, type TKey } from '../i18n';
import { useRunStore } from '../store/runStore';
import { coachmarkSeen, markCoachmarkSeen } from './coachmarks';

const SHOW_MS = 3200;

type ActiveCoachmark = 'door_walk' | 'joystick_move' | 'level_up' | 'hazard' | null;

/** One-time in-run contextual hints. */
export function InRunCoachmarks() {
  const { t } = useT();
  const [active, setActive] = useState<ActiveCoachmark>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const phase = useRunStore((s) => s.phase);

  function showCoachmark(id: Exclude<ActiveCoachmark, null>) {
    if (coachmarkSeen(id)) return;
    markCoachmarkSeen(id);
    if (timerRef.current) clearTimeout(timerRef.current);
    setActive(id);
    timerRef.current = setTimeout(() => setActive(null), SHOW_MS);
  }

  // Door-walk and event-driven coachmarks
  useEffect(() => {
    const onDoorOpened = () => showCoachmark('door_walk');
    const onCombatStarted = () => showCoachmark('joystick_move');
    const onHazardSpawned = () => showCoachmark('hazard');

    gameEvents.on(EVT.DoorOpened, onDoorOpened);
    gameEvents.on(EVT.CombatStarted, onCombatStarted);
    gameEvents.on(EVT.HazardSpawned, onHazardSpawned);

    return () => {
      gameEvents.off(EVT.DoorOpened, onDoorOpened);
      gameEvents.off(EVT.CombatStarted, onCombatStarted);
      gameEvents.off(EVT.HazardSpawned, onHazardSpawned);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Level-up coachmark: watch store phase
  useEffect(() => {
    if (phase === 'level_up') {
      showCoachmark('level_up');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  if (!active) return null;

  const ICONS: Record<Exclude<ActiveCoachmark, null>, string> = {
    door_walk: '🚪',
    joystick_move: '🕹',
    level_up: '⬆',
    hazard: '☢',
  };

  const testId: Record<Exclude<ActiveCoachmark, null>, string> = {
    door_walk: 'coach-door-walk',
    joystick_move: 'coach-joystick-move',
    level_up: 'coach-level-up',
    hazard: 'coach-hazard',
  };

  return (
    <div
      data-testid={testId[active]}
      style={{
        position: 'fixed',
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 18%)',
        left: 0, right: 0,
        display: 'flex', justifyContent: 'center',
        zIndex: 7,
        pointerEvents: 'none',
        animation: 'fade-in 0.2s ease-out both',
      }}
    >
      <div
        className="label glow-primary"
        style={{
          background: 'rgba(0, 8, 32, 0.82)',
          border: '1px solid var(--fg-primary)',
          padding: 'var(--space-3) var(--space-4)',
          color: 'var(--fg-primary)',
          fontSize: '0.85rem',
          letterSpacing: '0.06em',
          textAlign: 'center',
          maxWidth: '22rem',
          textShadow: '0 0 4px #000',
        }}
      >
        {ICONS[active]} {t(`coach.${active}` as TKey)}
      </div>
    </div>
  );
}
